'use client';
import { faHeart as faHeartRegular } from '@fortawesome/pro-regular-svg-icons';
import { faCartShopping, faHeart } from '@fortawesome/pro-solid-svg-icons';
import { sendGTMEvent } from '@next/third-parties/google';
import { Button, Icon, IconButton, Image } from '@ui';
import { AnimatePresence, LazyMotion, m } from 'framer-motion';
import { FC } from 'react';
import FeatureFlag from '@/components/common/feature-flag/feature-flag';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import { useGetDiscountType } from '@/hooks/use-has-discount';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import useProductDetailPageContext from '@/hooks/use-product-detail-page-context';
import { useAppContext, useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { cn } from '@/utils';
import loadFramerMotionFeatures from '@/utils/framer-motion-features';
import { getCategoryId } from '@/utils/get-category-id';
import { selectMainImage } from '@/utils/select-main-image';
import FormattedPrice from '../../common/formatted-price/formatted-price';

interface Props {
  isVisibleDesktop: boolean;
  isVisibleMobile: boolean;
}

const Price = ({
  product,
  parentCategoryId,
  productId,
}: {
  product: { price: number; discountedPrice: number };
  parentCategoryId: string | undefined;
  productId: string;
}) => {
  const { getDiscountType } = useGetDiscountType();
  const discountType = getDiscountType(product, parentCategoryId, productId);

  if (discountType === 'NONE') {
    return <FormattedPrice size="2xl" price={product.price} productId={productId} showVatSuffix />;
  }

  if (discountType === 'PRODUCT') {
    return (
      <>
        <FormattedPrice
          price={product.price}
          size="xl"
          lineThrough
          color="gray"
          productId={productId}
        />
        <FormattedPrice
          price={product.discountedPrice}
          size="2xl"
          color="red"
          productId={productId}
          showVatSuffix
        />
      </>
    );
  }

  if (discountType === 'PRICE_LIST') {
    return (
      <>
        <FormattedPrice
          price={product.price}
          size="xl"
          lineThrough
          color="gray"
          productId={productId}
        />
        <FormattedPrice
          price={product.price}
          size="2xl"
          color="red"
          productId={productId}
          showVatSuffix
        />
      </>
    );
  }

  // COMPANY discount
  return (
    <>
      <FormattedPrice
        price={product.price}
        size="xl"
        lineThrough
        color="gray"
        productId={productId}
      />
      <FormattedPrice
        price={product.price}
        size="2xl"
        color="red"
        categoryId={parentCategoryId}
        productId={productId}
        showVatSuffix
      />
    </>
  );
};

const StickyHeader: FC<Props> = ({ isVisibleDesktop, isVisibleMobile }) => {
  const { t } = useTranslation();
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const { favorites, addProductToFavorites, removeProductFromFavorites, isFavoritePending } =
    useFavorites();
  const { addProductToMyCart, isPendingUpdateMyCartProductId } = useMyCart();

  const { product } = useProductDetailPageContext();

  const isInFavorites = !!favorites.products.find(p => p.id === product.id);
  const { keycloak } = useKeycloak();
  const { features, currency } = useCoreConfig();
  const isAuthenticated = keycloak?.authenticated;
  const shouldShowPrice = isAuthenticated || features.priceForGuests;
  const shouldAllowCart = isAuthenticated || features.cartForGuests;

  const parentCategoryId = getCategoryId(product.superCategories.map(category => category.id!));

  return (
    // `LazyMotion` + `m.*` defers framer-motion's heavy projection layer
    // until the sticky header actually animates in. The async features loader
    // (see `@/utils/framer-motion-features`) lets webpack split the animation
    // features into their own chunk, loaded after first paint.
    <LazyMotion features={loadFramerMotionFeatures} strict>
      <div>
        <AnimatePresence>
          {!isVisibleDesktop && (
            <m.div
              key="desktop-sticky-header"
              initial={{ transform: 'translateY(-300px)' }}
              animate={{ transform: 'translateY(0px)' }}
              exit={{ transform: 'translateY(-300px)' }}
              transition={{ type: 'ease-in' }}
              className="fixed right-0 left-0 z-40 container mx-auto hidden px-9 @lg:block"
            >
              <div className="flex w-full flex-row justify-between rounded-2xl bg-white p-3 shadow-lg">
                <span className="flex flex-row items-center gap-2">
                  <Image
                    src={selectMainImage(product.images)}
                    alt={product.name}
                    width={100}
                    height={100}
                    className="object-contain"
                  />
                  <span className="text-2xl font-bold">{product.name}</span>
                </span>
                <span className="flex flex-row items-center gap-4">
                  {shouldShowPrice && (
                    <Price
                      product={product}
                      parentCategoryId={parentCategoryId}
                      productId={product.id}
                    />
                  )}

                  <div className="flex gap-2">
                    {shouldAllowCart && (
                      <Button
                        size="md"
                        startContent={
                          isPendingUpdateMyCartProductId === product.id ? null : (
                            <Icon icon={faCartShopping} />
                          )
                        }
                        color="primary"
                        isDisabled={isStudioPreview}
                        onClick={() => {
                          if (isStudioPreview) return;

                          const currentEventId = crypto.randomUUID();
                          sendGTMEvent({ ecommerce: null });
                          sendGTMEvent({
                            event: 'add_to_cart',
                            event_id: currentEventId,
                            ecommerce: {
                              currency,
                              value: product.discountedPrice,
                              items: [
                                {
                                  item_id: product.id,
                                  item_name: product.name,
                                  price: product.discountedPrice,
                                  quantity: 1,
                                  item_category: product.superCategories?.[0]?.name || '',
                                },
                              ],
                            },
                          });
                          addProductToMyCart(
                            {
                              ...product,
                              parentCategoryId: '',
                              discountedPriceType: 'NONE',
                              quantity: 1,
                            },
                            {
                              name: 'AddToCart',
                              id: currentEventId,
                              sourceUrl: window.location.href,
                            },
                          );
                        }}
                        isLoading={isPendingUpdateMyCartProductId === product.id}
                      >
                        {t?.productDetail.addToCard}
                      </Button>
                    )}
                    <FeatureFlag feature="favorites">
                      <IconButton
                        aria-label="Toggle favorite"
                        size="md"
                        variant="bordered"
                        isLoading={isFavoritePending(product.id)}
                        isDisabled={isStudioPreview}
                        onClick={() => {
                          if (isStudioPreview) return;

                          if (isInFavorites) {
                            const currentEventId = crypto.randomUUID();
                            sendGTMEvent({ ecommerce: null });
                            sendGTMEvent({
                              event: 'remove_from_favorites',
                              event_id: currentEventId,
                              ecommerce: {
                                currency,
                                value: product.discountedPrice,
                                items: [
                                  {
                                    item_id: product.id,
                                    item_name: product.name,
                                    price: product.discountedPrice,
                                    item_category: product.superCategories?.[0]?.name || '',
                                  },
                                ],
                              },
                            });
                            removeProductFromFavorites(
                              {
                                ...product,
                                parentCategoryId: '',
                                discountedPriceType: 'NONE',
                              },
                              {
                                name: 'RemoveFromWishlist',
                                id: currentEventId,
                                sourceUrl: window.location.href,
                              },
                            );
                          } else {
                            const currentEventId = crypto.randomUUID();
                            sendGTMEvent({ ecommerce: null });
                            sendGTMEvent({
                              event: 'add_to_favorites',
                              event_id: currentEventId,
                              ecommerce: {
                                currency,
                                value: product.discountedPrice,
                                items: [
                                  {
                                    item_id: product.id,
                                    item_name: product.name,
                                    price: product.discountedPrice,
                                    item_category: product.superCategories?.[0]?.name || '',
                                  },
                                ],
                              },
                            });
                            addProductToFavorites(
                              {
                                ...product,
                                parentCategoryId: '',
                                discountedPriceType: 'NONE',
                              },
                              {
                                name: 'AddToWishlist',
                                id: currentEventId,
                                sourceUrl: window.location.href,
                              },
                            );
                          }
                        }}
                      >
                        <m.div whileTap={{ scale: 1.4 }}>
                          <Icon
                            icon={isInFavorites ? faHeart : faHeartRegular}
                            className={cn('!text-base', { 'text-red-600': isInFavorites })}
                          />
                        </m.div>
                      </IconButton>
                    </FeatureFlag>
                  </div>
                </span>
              </div>
            </m.div>
          )}
          {!isVisibleMobile && (
            <m.div
              key="mobile-sticky-header"
              initial={{ transform: 'translateY(-300px)' }}
              animate={{ transform: 'translateY(0px)' }}
              exit={{ transform: 'translateY(-300px)' }}
              transition={{ type: 'ease-in' }}
              className="fixed right-0 left-0 z-40 container mx-auto block px-9 @lg:hidden"
            >
              <div className="flex w-full flex-row gap-2 rounded-2xl bg-white p-3 shadow-lg md:justify-between">
                <div className="block">
                  <Image
                    src={selectMainImage(product.images)}
                    alt={product.name}
                    width={100}
                    height={100}
                    className="object-contain"
                  />
                </div>
                <span className="flex w-full flex-col items-center justify-center gap-2">
                  <span className="text-medium block self-start font-bold">{product.name}</span>
                  <div className="flex w-full flex-row items-center justify-between gap-4">
                    {shouldShowPrice && (
                      <Price
                        product={product}
                        parentCategoryId={parentCategoryId}
                        productId={product.id}
                      />
                    )}
                    <div className={cn('flex gap-2', !shouldAllowCart && 'hidden')}>
                      {shouldAllowCart && (
                        <Button
                          aria-label="Add to cart"
                          isIconOnly
                          color="primary"
                          className="text-white"
                          isDisabled={isStudioPreview}
                          onClick={e => {
                            if (isStudioPreview) return;

                            const currentEventId = crypto.randomUUID();
                            e.preventDefault();
                            sendGTMEvent({ ecommerce: null });
                            sendGTMEvent({
                              event: 'add_to_cart',
                              event_id: currentEventId,
                              ecommerce: {
                                currency,
                                value: product.discountedPrice,
                                items: [
                                  {
                                    item_id: product.id,
                                    item_name: product.name,
                                    price: product.discountedPrice,
                                    quantity: 1,
                                    item_category: product.superCategories?.[0]?.name || '',
                                  },
                                ],
                              },
                            });
                            addProductToMyCart(
                              {
                                ...product,
                                parentCategoryId: '',
                                discountedPriceType: 'NONE',
                                quantity: 1,
                              },
                              {
                                name: 'AddToCart',
                                id: currentEventId,
                                sourceUrl: window.location.href,
                              },
                            );
                          }}
                          isLoading={isPendingUpdateMyCartProductId === product.id}
                        >
                          <Icon icon={faCartShopping} />
                        </Button>
                      )}
                      <FeatureFlag feature="favorites">
                        <IconButton
                          aria-label="Toggle favorite"
                          size="md"
                          variant="bordered"
                          isDisabled={isStudioPreview}
                          onClick={() => {
                            if (isStudioPreview) return;

                            if (isInFavorites) {
                              const currentEventId = crypto.randomUUID();
                              sendGTMEvent({ ecommerce: null });
                              sendGTMEvent({
                                event: 'remove_from_favorites',
                                event_id: currentEventId,
                                ecommerce: {
                                  currency,
                                  value: product.discountedPrice,
                                  items: [
                                    {
                                      item_id: product.id,
                                      item_name: product.name,
                                      price: product.discountedPrice,
                                      item_category: product.superCategories?.[0]?.name || '',
                                    },
                                  ],
                                },
                              });
                              removeProductFromFavorites(
                                {
                                  ...product,
                                  parentCategoryId: '',
                                  discountedPriceType: 'NONE',
                                },
                                {
                                  name: 'RemoveFromWishlist',
                                  id: currentEventId,
                                  sourceUrl: window.location.href,
                                },
                              );
                            } else {
                              const currentEventId = crypto.randomUUID();
                              sendGTMEvent({ ecommerce: null });
                              sendGTMEvent({
                                event: 'add_to_favorites',
                                event_id: currentEventId,
                                ecommerce: {
                                  currency,
                                  value: product.discountedPrice,
                                  items: [
                                    {
                                      item_id: product.id,
                                      item_name: product.name,
                                      price: product.discountedPrice,
                                      item_category: product.superCategories?.[0]?.name || '',
                                    },
                                  ],
                                },
                              });
                              addProductToFavorites(
                                {
                                  ...product,
                                  parentCategoryId: '',
                                  discountedPriceType: 'NONE',
                                },
                                {
                                  name: 'AddToWishlist',
                                  id: currentEventId,
                                  sourceUrl: window.location.href,
                                },
                              );
                            }
                          }}
                          isLoading={isFavoritePending(product.id)}
                        >
                          <Icon
                            icon={isInFavorites ? faHeart : faHeartRegular}
                            className={cn('!text-base', { 'text-red-600': isInFavorites })}
                          />
                        </IconButton>
                      </FeatureFlag>
                    </div>
                  </div>
                </span>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </LazyMotion>
  );
};

export default StickyHeader;
